import { Trans } from '@lingui/macro';
import { GitHub, Instagram, LinkedIn, X } from '@mui/icons-material';
import { Box, styled, SvgIcon, Typography } from '@mui/material';
import { DuneIcon, TikTok } from 'public/icons/footer/icons';
import { Link } from 'src/components/primitives/Link';
import { useRootStore } from 'src/store/root';
import { useShallow } from 'zustand/shallow';

import DiscordIcon from '/public/icons/discord.svg';
import LensLogoIcon from '/public/icons/lens-logo.svg';

interface StyledLinkProps {
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

const StyledLink = styled(Link)<StyledLinkProps>(({ theme }) => ({
  color: theme.palette.text.muted,
  '&:hover': {
    color: theme.palette.text.primary,
  },
  display: 'flex',
  alignItems: 'center',
}));

const FOOTER_ICONS = [
  {
    href: 'https://x.com/',
    icon: <X />,
    title: 'Twitter',
  },
  {
    href: 'https://www.instagram.com/',
    icon: <Instagram />,
    title: 'Instagram',
  },
  {
    href: 'https://www.tiktok.com/',
    icon: <TikTok />,
    title: 'TikTok',
  },
  {
    href: 'https://www.linkedin.com/',
    icon: <LinkedIn />,
    title: 'Linkedin',
  },
  {
    href: '#',
    icon: <DiscordIcon />,
    title: 'Discord',
  },
  {
    href: 'https://dune.com/',
    icon: <DuneIcon />,
    title: 'Dune',
  },
  {
    href: 'https://github.com/',
    icon: <GitHub />,
    title: 'Github',
  },
  {
    href: 'https://hey.xyz/',
    icon: <LensLogoIcon />,
    title: 'Dinolend on Lens',
  },
];

export function AppFooter() {
  const [setAnalyticsConfigOpen, setFeedbackOpen] = useRootStore(
    useShallow((store) => [store.setAnalyticsConfigOpen, store.setFeedbackOpen])
  );

  const FOOTER_LINKS = [
    {
      href: '#',
      label: <Trans>Terms</Trans>,
      key: 'Terms',
    },
    {
      href: '#',
      label: <Trans>Privacy</Trans>,
      key: 'Privacy',
    },
    {
      href: '#',
      label: <Trans>Docs</Trans>,
      key: 'Docs',
    },
    {
      href: '#',
      label: <Trans>FAQS</Trans>,
      key: 'FAQS',
    },
    {
      href: '#',
      label: <Trans>Get Support</Trans>,
      key: 'Get Support',
      onClick: (event: React.MouseEvent) => {
        event.preventDefault();
        setFeedbackOpen(true);
      },
    },
    {
      href: '/',
      label: <Trans>Manage analytics</Trans>,
      key: 'Manage analytics',
      onClick: (event: React.MouseEvent) => {
        event.preventDefault();
        setAnalyticsConfigOpen(true);
      },
    },
  ];

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        padding: ['22px 0px 40px 0px', '0 22px 0 40px', '20px 22px'],
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '22px',
        flexDirection: ['column', 'column', 'row'],
        boxShadow:
          theme.palette.mode === 'light'
            ? 'inset 0px 1px 0px rgba(0, 0, 0, 0.04)'
            : 'inset 0px 1px 0px rgba(255, 255, 255, 0.12)',
      })}
    >
      <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {FOOTER_LINKS.map((link) => (
          <StyledLink onClick={link.onClick} key={link.key} href={link.href}>
            <Typography variant="caption">{link.label}</Typography>
          </StyledLink>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {FOOTER_ICONS.map((icon) => (
          <StyledLink href={icon.href} key={icon.title}>
            <SvgIcon
              sx={{
                fontSize: [24, 24, 20],
              }}
            >
              {icon.icon}
            </SvgIcon>
          </StyledLink>
        ))}
      </Box>
    </Box>
  );
}
